/* eslint-disable import/no-unresolved */
import React, { useEffect, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import Modal from 'reactjs-popup';
import markerIconPng from 'leaflet/dist/images/marker-icon.png';
import { Icon } from 'leaflet';
import Button from '~/components/button';
import ContainerFlex from '~/components/containerFlex';
import Loading from '~/components/loading';
import 'leaflet/dist/leaflet.css';
import 'reactjs-popup/dist/index.css';

type LatLng = [number, number];

// Centro aproximado do Brasil, usado como fallback quando não há localização.
const DEFAULT_CENTER: LatLng = [-14.235, -51.9253];
const DEFAULT_ZOOM = 4;
const LOCATED_ZOOM = 18;

const markerIcon = new Icon({
  iconUrl: markerIconPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// Recentraliza o mapa quando o centro/zoom mudam e corrige o tamanho do
// canvas ao abrir dentro do modal.
const RecenterMap = ({ center, zoom }: { center: LatLng; zoom: number }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom);
    const timer = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(timer);
  }, [center, zoom]);
  return null;
};

// Captura o clique no mapa e devolve a latitude/longitude selecionadas.
const ClickHandler = ({
  onSelect,
}: {
  onSelect: (latLng: LatLng) => void;
}) => {
  useMapEvents({
    click(e) {
      onSelect([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
};

interface MapLocationModalProps {
  open: boolean;
  onClose: () => void;
  searchQuery?: string;
  initialLatLng?: LatLng | null;
  onConfirm: (latitude: number, longitude: number) => void;
}

const MapLocationModal = ({
  open,
  onClose,
  searchQuery,
  initialLatLng,
  onConfirm,
}: MapLocationModalProps) => {
  const [center, setCenter] = useState<LatLng>(
    initialLatLng ?? DEFAULT_CENTER,
  );
  const [zoom, setZoom] = useState<number>(
    initialLatLng ? LOCATED_ZOOM : DEFAULT_ZOOM,
  );
  const [marker, setMarker] = useState<LatLng | null>(initialLatLng ?? null);
  const [loading, setLoading] = useState(false);

  const geocodeAddress = async () => {
    if (!searchQuery?.trim()) return;
    try {
      setLoading(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=${encodeURIComponent(
          searchQuery,
        )}`,
        { headers: { 'Accept-Language': 'pt-BR' } },
      );
      const data = await response.json();
      if (data?.length) {
        const located: LatLng = [
          parseFloat(data[0].lat),
          parseFloat(data[0].lon),
        ];
        setCenter(located);
        setMarker(located);
        setZoom(LOCATED_ZOOM);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Solicita a permissão de localização do navegador para posicionar o mapa
  // no ponto exato. Se for negada/indisponível, cai no endereço geocodificado.
  const requestBrowserLocation = () => {
    if (!navigator.geolocation) {
      geocodeAddress();
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const located: LatLng = [
          position.coords.latitude,
          position.coords.longitude,
        ];
        setCenter(located);
        setMarker(located);
        setZoom(LOCATED_ZOOM);
        setLoading(false);
      },
      (error) => {
        console.log(error);
        setLoading(false);
        geocodeAddress();
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  useEffect(() => {
    if (!open) return;
    if (initialLatLng) {
      setCenter(initialLatLng);
      setMarker(initialLatLng);
      setZoom(LOCATED_ZOOM);
    } else {
      requestBrowserLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = () => {
    if (marker) onConfirm(marker[0], marker[1]);
    onClose();
  };

  return (
    <Modal
      position="center center"
      closeOnDocumentClick
      onClose={onClose}
      open={open}
      contentStyle={{ width: '50vw', minWidth: '600px', maxWidth: '90vw', borderRadius: 8 }}
    >
      <Loading status={loading} />
      <ContainerFlex row center horizontal>
        <p style={{ fontWeight: 'bold', fontSize: '18px', margin: 8 }}>
          Selecione a localização no mapa
        </p>
      </ContainerFlex>
      <ContainerFlex row center horizontal marginBottom={8}>
        <span style={{ fontSize: '13px', color: '#666' }}>
          Clique no mapa para marcar o ponto exato do estabelecimento.
        </span>
      </ContainerFlex>
      {open && (
        <MapContainer
          id="map-location"
          style={{ height: '50vh', width: '100%' }}
          center={center}
          zoom={zoom}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterMap center={center} zoom={zoom} />
          <ClickHandler onSelect={setMarker} />
          {marker && (
            <Marker icon={markerIcon} position={marker}>
              <Popup>Localização selecionada</Popup>
            </Marker>
          )}
        </MapContainer>
      )}
      {marker && (
        <ContainerFlex row center horizontal marginTop={8}>
          <span style={{ fontSize: '14px' }}>
            Latitude: {marker[0].toFixed(6)} | Longitude: {marker[1].toFixed(6)}
          </span>
        </ContainerFlex>
      )}
      <ContainerFlex row end gap="16" marginBottom={8} marginTop={16}>
        <Button outlined fitContent onClick={onClose}>
          Voltar
        </Button>
        <Button fitContent onClick={handleConfirm} disabled={!marker}>
          Confirmar localização
        </Button>
      </ContainerFlex>
    </Modal>
  );
};

export default MapLocationModal;
